import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientApiService, ClientLead, LeadFilters } from '../services/client.api';
import { isAuthenticated } from '../auth';
import './ClientLeadsPage.css';

const COLUMNS = [
  { key: 'id', label: 'ID', defaultWidth: 60 },
  { key: 'date', label: 'Дата', defaultWidth: 110 },
  { key: 'phone', label: 'Телефон', defaultWidth: 150 },
  { key: 'name', label: 'Имя', defaultWidth: 150 },
  { key: 'disputComment', label: 'Комментарий спора', defaultWidth: 350 },
  { key: 'clientManager', label: 'Менеджер клиента', defaultWidth: 200 },
  { key: 'status', label: 'Статус', defaultWidth: 160 },
  { key: 'actions', label: 'Действия', defaultWidth: 180 },
];

const COL_WIDTHS_KEY = 'client-leads-column-widths';

const loadColumnWidths = (): Record<string, number> => {
  try {
    const saved = localStorage.getItem(COL_WIDTHS_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      const result: Record<string, number> = {};
      COLUMNS.forEach(col => {
        result[col.key] = typeof parsed[col.key] === 'number' ? parsed[col.key] : col.defaultWidth;
      });
      return result;
    }
  } catch { /* ignore */ }
  return Object.fromEntries(COLUMNS.map(col => [col.key, col.defaultWidth]));
};

type LeadStatus = 'UNPROCESSED' | 'AT_WORK' | 'ASSIGNED' | 'HAPPENED' | 'INVALID' | 'NON_CALL' | 'ACCEPT_DEAL' | 'REPLACED' | 'BONUS';

interface LeadStatusTab {
  key: LeadStatus;
  label: string;
  count: number;
}

const ClientLeadsPage: React.FC = () => {
  const [leads, setLeads] = useState<ClientLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<LeadStatus>('UNPROCESSED');
  const [filters, setFilters] = useState<LeadFilters>({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(loadColumnWidths);
  const navigate = useNavigate();

  const handleResizeStart = (key: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = columnWidths[key];
    document.body.classList.add('col-resizing');

    const onMove = (moveEvent: MouseEvent) => {
      const diff = moveEvent.clientX - startX;
      const newWidth = Math.max(50, startWidth + diff);
      setColumnWidths(prev => ({ ...prev, [key]: newWidth }));
    };

    const onEnd = () => {
      document.body.classList.remove('col-resizing');
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
      setColumnWidths(prev => {
        localStorage.setItem(COL_WIDTHS_KEY, JSON.stringify(prev));
        return prev;
      });
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onEnd);
  };

    const loadLeads = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await clientApiService.getClientLeads({
        ...filters,
        status: activeTab,
        page: pagination.page,
        limit: pagination.limit,
      });
      
      if (response.leads) {
        setLeads(response.leads);
        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        setError(response.error || 'Ошибка загрузки лидов');
      }
    } catch (error: any) {
      setError(error.message || 'Произошла ошибка при загрузке данных');
    } finally {
      setLoading(false);
    }
  }, [filters, activeTab, pagination.page, pagination.limit]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    loadLeads();
  }, [navigate, loadLeads]);

  const handleTabChange = (status: LeadStatus) => {
    setActiveTab(status);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (newFilters: Partial<LeadFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleUpdateLead = async (leadId: number, data: {
    disputComment?: string;
    clientManagerName?: string;
    status?: LeadStatus;
  }) => {
    try {
      const response = await clientApiService.updateByClient(leadId, data);
      
      if (response) {
        loadLeads(); // Перезагружаем данные
      } else {
        setError('Ошибка обновления лида');
      }
    } catch (error: any) {
      setError(error.message || 'Произошла ошибка при обновлении лида');
    }
  };

  const getStatusLabel = (status: LeadStatus) => {
    const statusLabels: Record<LeadStatus, string> = {
      'UNPROCESSED': 'Необработанные',
      'AT_WORK': 'В работе',
      'ASSIGNED': 'Назначены',
      'HAPPENED': 'Произошли',
      'INVALID': 'Недействительные',
      'NON_CALL': 'Не дозвонились',
      'ACCEPT_DEAL': 'Сделки',
      'REPLACED': 'Заменены',
      'BONUS': 'Бонусные',
    };
    return statusLabels[status];
  };

  const getStatusColor = (status: LeadStatus) => {
    const statusColors: Record<LeadStatus, string> = {
      'UNPROCESSED': 'default',
      'AT_WORK': 'warning',
      'ASSIGNED': 'info',
      'HAPPENED': 'success',
      'INVALID': 'danger',
      'NON_CALL': 'info',
      'ACCEPT_DEAL': 'success',
      'REPLACED': 'secondary',
      'BONUS': 'bonus',
    };
    return statusColors[status];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  const statusTabs: LeadStatusTab[] = [
    { key: 'UNPROCESSED', label: 'Необработанные', count: 0 },
    { key: 'AT_WORK', label: 'В работе', count: 0 },
    { key: 'ASSIGNED', label: 'Назначены', count: 0 },
    { key: 'HAPPENED', label: 'Произошли', count: 0 },
    { key: 'INVALID', label: 'Недействительные', count: 0 },
    { key: 'NON_CALL', label: 'Не дозвонились', count: 0 },
    { key: 'ACCEPT_DEAL', label: 'Сделки', count: 0 },
    { key: 'REPLACED', label: 'Заменены', count: 0 },
    { key: 'BONUS', label: 'Бонусные', count: 0 },
  ];

  if (loading) {
    return (
      <div className="client-leads-loading">
        <div className="loading-spinner"></div>
        <p>Загрузка данных...</p>
      </div>
    );
  }

  return (
    <div className="client-leads">
      {/* Header */}
      <header className="client-leads-header">
        <div className="client-leads-header-left">
          <h1>Мои лиды</h1>
        </div>
        <div className="client-leads-header-right">
          <div className="search-filter">
            <input
              type="text"
              placeholder="Поиск по телефону..."
              value={filters.phone_search || ''}
              onChange={(e) => handleFilterChange({ phone_search: e.target.value })}
              className="search-input"
            />
          </div>
        </div>
      </header>

      {/* Status Tabs */}
      <div className="status-tabs">
        {statusTabs.map((tab) => (
          <button
            key={tab.key}
            className={`status-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.key)}
          >
            {tab.label}
            {tab.count > 0 && <span className="tab-count">({tab.count})</span>}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="client-leads-error">
          {error}
        </div>
      )}

      {/* Leads Table */}
      <div className="client-leads-table">
        <table style={{ minWidth: Object.values(columnWidths).reduce((a, b) => a + b, 0) }}>
          <thead>
            <tr>
              {COLUMNS.map(col => (
                <th key={col.key} style={{ width: columnWidths[col.key] }}>
                  {col.label}
                  <div
                    className="resize-handle"
                    onMouseDown={(e) => handleResizeStart(col.key, e)}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leads.map((lead) => (
              <tr key={lead.id}>
                <td>{lead.id}</td>
                <td>{formatDate(lead.created_at)}</td>
                <td>{lead.phone}</td>
                <td>{lead.customer_name || '-'}</td>
                <td>
                  <div className="disput-comment-cell">
                    {lead.disput_comment ? (
                      <div className="disput-comment-text">{lead.disput_comment}</div>
                    ) : (
                      <button
                        onClick={() => {
                          const comment = prompt('Введите комментарий спора:');
                          if (comment) {
                            handleUpdateLead(lead.id, { disputComment: comment });
                          }
                        }}
                        className="btn-add-comment"
                      >
                        Добавить комментарий
                      </button>
                    )}
                  </div>
                </td>
                <td>
                  <div className="client-manager-cell">
                    {lead.client_manager_name ? (
                      <div className="client-manager-text">{lead.client_manager_name}</div>
                    ) : (
                      <button
                        onClick={() => {
                          const manager = prompt('Введите имя менеджера клиента:');
                          if (manager) {
                            handleUpdateLead(lead.id, { clientManagerName: manager });
                          }
                        }}
                        className="btn-add-manager"
                      >
                        Добавить менеджера
                      </button>
                    )}
                  </div>
                </td>
                <td>
                  <span className={`status-badge ${getStatusColor(lead.status as LeadStatus)}`}>
                    {getStatusLabel(lead.status as LeadStatus)}
                  </span>
                </td>
                <td>
                  <div className="actions-cell">
                    <select
                      value={lead.status}
                      onChange={(e) => handleUpdateLead(lead.id, { status: e.target.value as LeadStatus })}
                      className="status-select"
                    >
                      <option value="UNPROCESSED">Необработанный</option>
                      <option value="AT_WORK">В работе</option>
                      <option value="ASSIGNED">Назначен</option>
                      <option value="HAPPENED">Произошел</option>
                      <option value="INVALID">Недействительный</option>
                      <option value="NON_CALL">Не дозвонились</option>
                      <option value="ACCEPT_DEAL">Сделка</option>
                      <option value="REPLACED">Заменен</option>
                      <option value="BONUS">Бонусный</option>
                    </select>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            disabled={pagination.page <= 1}
            className="pagination-btn"
          >
            ← Предыдущая
          </button>
          <span className="pagination-info">
            Страница {pagination.page} из {pagination.totalPages}
          </span>
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            disabled={pagination.page >= pagination.totalPages}
            className="pagination-btn"
          >
            Следующая →
          </button>
        </div>
      )}

      {leads.length === 0 && (
        <div className="no-leads">
          <p>Нет лидов для отображения</p>
        </div>
      )}
    </div>
  );
};

export default ClientLeadsPage; 